/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState } from "react";
import { IconChevronDown, IconX } from "@tabler/icons-react";
import { Controller, UseFormClearErrors, Control } from "react-hook-form";
import RaidoField from "../../../components/common/RaidoField";
import CreateFloorModal from "./CreateFloorModal";
import { useLanguage } from "../../../context/LanguageContext";

// Define interfaces for form data and props
interface RowFormData {
  name: string;
  rowNumbers: string;
}

interface AssignDriversModalProps {
  open: boolean;
  onClose: () => void;
  register: any;
  setValue: any;
  watch: any;
  errors: any;
  clearErrors?: UseFormClearErrors<RowFormData>;
  control: Control<RowFormData>;
  zoneItem?: any;
  rackInstanceId: string;
  shelveId: any;
  warehouseId: string;
  clientId: string;
  fetchShelves: () => void;
  rowId: string;
  modalType?: string;
  handleSubmit: React.MouseEventHandler<HTMLButtonElement>;
  isPending: boolean;
}

interface CustomSelectProps {
  namePrefix: string;
  nameNumber: string;
  control: Control<RowFormData>;
  labeltext: string;
  options: string[];
  placeholder?: string;
  prefix: string;
  setPrefix: (val: string) => void;
  error?: string;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
  namePrefix,
  nameNumber,
  control,
  labeltext,
  options,
  placeholder = "Select...",

  prefix,
  setPrefix,
  error,
}) => {
  const [open, setOpen] = useState(false);
  const { t } = useLanguage();

  return (
    <div className="space-y-1">
      <label className="text-[#1E293B] text-sm">{t(labeltext)}</label>
      <div className="flex items-center justify-between px-4 py-1.5 text-sm rounded-full border border-gray-200 bg-white gap-2">
        <Controller
          name={namePrefix as keyof RowFormData}
          control={control}
          rules={{
            required: t(`${labeltext} prefix is required`),
            minLength: {
              value: 2,
              message: t(`${labeltext} prefix must be at least 2 characters`),
            },
            maxLength: {
              value: 3,
              message: t(`${labeltext} prefix must be 3 characters or less`),
            },
          }}
          render={({ field }) => (
            <input
              type="text"
              value={prefix}
              placeholder={t("Enter name")}
              maxLength={3}
              onChange={(e) => {
                setPrefix(e.target.value);
                field.onChange(e.target.value);
              }}
              className="focus:outline-none w-20"
            />
          )}
        />

        <Controller
          name={nameNumber as keyof RowFormData}
          control={control}
          rules={{ required: t(`${labeltext} number is required`) }}
          render={({ field }) => (
            <div className="relative">
              <button
                type="button"
                className="w-24 flex justify-between items-center border border-gray-300 rounded-lg p-2 py-0.5 bg-white text-gray-700 gap-1"
                onClick={() => setOpen(!open)}
              >
                <span className="font-medium text-[#212121]">
                  {field.value || t(placeholder)}
                </span>
                <IconChevronDown
                  className={`w-4 h-4 transition-transform ${
                    open ? "rotate-180" : ""
                  }`}
                />
              </button>
              {open && (
                <ul className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-auto">
                  {options.map((opt, idx) => (
                    <li
                      key={idx}
                      onClick={() => {
                        field.onChange(opt);
                        setOpen(false);
                      }}
                      className="px-3 py-2 cursor-pointer hover:bg-gray-100"
                    >
                      {opt}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        />
      </div>
      {error && <p className="text-red-500 text-xs">{t(error)}</p>}
    </div>
  );
};

export default function CreateRowOnlyModal({
  open,
  onClose,
  errors,
  modalType,
  watch,
  rackInstanceId,
  shelveId,
  warehouseId,
  clientId,
  fetchShelves,
  rowId,
  control,
  isPending,
  handleSubmit,
}: AssignDriversModalProps) {
  const [rackType, setRackType] = useState("");
  const [levelType, setLevelType] = useState("");
  const [modeLocal, setModeLocal] = useState("rowLevel");
  const { t } = useLanguage();

  // In edit mode, always use rowLevel and hide the radio
  const isEditMode = modalType === "edit";
  const effectiveMode = isEditMode ? "rowLevel" : modeLocal;

  if (!open) return null;

  return (
    // <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
    <div className="bg-[#F7F6F2] rounded-3xl  flex flex-col">
      <div className="flex items-center justify-between px-4 py-3 border-b">
        <h2 className="text-lg font-medium text-[#212121]">
          {modalType === "edit" ? t("Edit Row") : t("Create Row")}
        </h2>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-full border-[0.5px] border-[#D2D2D2]"
        >
          <IconX size={20} />
        </button>
      </div>

      <div className="flex-1 px-4 py-2 space-y-6">
        {effectiveMode === "rowLevel" && (
          <>
            <div className="grid grid-cols-1 gap-4">
              <CustomSelect
                namePrefix="name"
                nameNumber="rowNumbers"
                control={control}
                labeltext="Row"
                options={["1", "2", "3", "4", "5", "6", "7", "8"]}
                prefix={rackType}
                setPrefix={setRackType}
                error={errors?.rowNumbers?.message}
              />
            </div>
            <div className="flex justify-end space-x-2 px-4 py-2 border-t">
              <button
                onClick={handleSubmit}
                disabled={isPending}
                className="px-4 py-2 rounded-full bg-[#FBD037] text-black font-medium hover:bg-yellow-500 transition flex items-center justify-center"
              >
                {isPending ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>{modalType === "edit" ? t("Update") : t("Create")}</>
                )}
              </button>
            </div>
          </>
        )}
        {effectiveMode === "floor" && (
          <CreateFloorModal shelveId={shelveId} fetchShelves={fetchShelves} />
        )}
      </div>
    </div>
    // </div>
  );
}
